import { useTranslation } from 'react-i18next';
import { usePreferences } from '@shared/model/preferences';
import { intlLocaleWithNumerals, isLocale, DEFAULT_LOCALE, type Locale, type Numerals } from './locales';

export type DateInput = Date | string | number;
export type DatePreset = 'dateShort' | 'dateMedium' | 'weekdayDate' | 'time' | 'dateTimeMedium' | 'dateTimeFull';

const PRESETS: Record<DatePreset, Intl.DateTimeFormatOptions> = {
  dateShort: { day: 'numeric', month: 'short' },
  dateMedium: { day: 'numeric', month: 'long', year: 'numeric' },
  weekdayDate: { weekday: 'long', day: 'numeric', month: 'long' },
  time: { hour: '2-digit', minute: '2-digit' },
  dateTimeMedium: { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' },
  dateTimeFull: { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' },
};

/** 24 h in Arabic and French, 12 h in English (design system §8). Always hourCycle, never hour12. */
export function defaultHourCycle(locale: Locale): 'h12' | 'h23' {
  return locale === 'en' ? 'h12' : 'h23';
}

export function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value);
}

export interface FormatterContext {
  locale: Locale;
  numerals: Numerals;
  /** Viewer timezone (IANA). */
  timeZone: string;
}

export interface Formatters extends FormatterContext {
  intlLocale: string;
  number: (value: number, options?: Intl.NumberFormatOptions) => string;
  percent: (value: number, fractionDigits?: number) => string;
  date: (value: DateInput, preset?: DatePreset, timeZone?: string) => string;
  time: (value: DateInput, timeZone?: string) => string;
  /** "GMT+3" style offset label for a zone at a given instant. */
  offsetLabel: (timeZone: string, at?: DateInput) => string;
  /** Human city label from an IANA id ("America/New_York" → "New York"). */
  zoneCity: (timeZone: string) => string;
  /** Locale-aware list ("A, B and C" / «A وB وC»). */
  list: (items: readonly string[]) => string;
}

const numberCache = new Map<string, Intl.NumberFormat>();
const dateCache = new Map<string, Intl.DateTimeFormat>();

function numberFormat(tag: string, options: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  const key = `${tag}|${JSON.stringify(options)}`;
  let f = numberCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(tag, options);
    numberCache.set(key, f);
  }
  return f;
}

function dateFormat(tag: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${tag}|${JSON.stringify(options)}`;
  let f = dateCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(tag, options);
    dateCache.set(key, f);
  }
  return f;
}

export function offsetLabel(timeZone: string, at: DateInput = new Date(), tag = 'en-GB'): string {
  try {
    const part = dateFormat(tag, { timeZone, timeZoneName: 'shortOffset' })
      .formatToParts(toDate(at))
      .find((p) => p.type === 'timeZoneName');
    return part?.value ?? timeZone;
  } catch {
    return timeZone;
  }
}

/** True when two zones show the same wall-clock offset at the given instant. */
export function sameOffset(a: string, b: string, at: DateInput = new Date()): boolean {
  if (a === b) return true;
  return offsetLabel(a, at) === offsetLabel(b, at);
}

export function zoneCity(timeZone: string): string {
  const last = timeZone.split('/').pop() ?? timeZone;
  return last.replace(/_/g, ' ');
}

export function createFormatters(ctx: FormatterContext): Formatters {
  const tag = intlLocaleWithNumerals(ctx.locale, ctx.numerals);
  const hourCycle = defaultHourCycle(ctx.locale);
  return {
    ...ctx,
    intlLocale: tag,
    number: (value, options) => numberFormat(tag, options).format(value),
    percent: (value, fractionDigits = 0) =>
      numberFormat(tag, { style: 'percent', maximumFractionDigits: fractionDigits, minimumFractionDigits: fractionDigits }).format(
        value / 100,
      ),
    date: (value, preset = 'dateMedium', timeZone) => {
      const options: Intl.DateTimeFormatOptions = { ...PRESETS[preset], timeZone: timeZone ?? ctx.timeZone };
      if (options.hour) options.hourCycle = hourCycle;
      return dateFormat(tag, options).format(toDate(value));
    },
    time: (value, timeZone) =>
      dateFormat(tag, { ...PRESETS.time, hourCycle, timeZone: timeZone ?? ctx.timeZone }).format(toDate(value)),
    offsetLabel: (timeZone, at) => offsetLabel(timeZone, at, tag),
    zoneCity,
    list: (items) => new Intl.ListFormat(tag, { type: 'conjunction', style: 'long' }).format(items),
  };
}

/** The single entry point for number/date formatting in components (i18n doc §4.2). */
export function useFormatters(): Formatters {
  const { i18n } = useTranslation();
  const numerals = usePreferences((s) => s.numerals);
  const timeZone = usePreferences((s) => s.timeZone);
  const lng = i18n.resolvedLanguage ?? i18n.language;
  const locale = isLocale(lng) ? lng : DEFAULT_LOCALE;
  return createFormatters({ locale, numerals, timeZone });
}
